import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import GameSetup from './GameSetup';
import GameActive from './GameActive';
import GameOver from './GameOver';
import useGameStore from '../store/gameStore';

interface GameControlsProps {
  selectedModel: string;
  useVision: boolean;
  playerColor: 'white' | 'black' | 'random';
  gameTimeLimit: 3 | 5 | 10;
  game: any;
  boardOrientation: 'white' | 'black';
  isGameOver: boolean;
  isCheckmate: boolean;
  isDraw: boolean;
  isGameActive: boolean;
  playerTimeRemaining: number;
  modelTimeRemaining: number;
  timeOut: 'player' | 'model' | null;
  activePlayer: 'player' | 'model';
  onModelChange: (model: string) => void;
  onModelWithVisionChange: (model: string, useVision: boolean) => void;
  onPlayerColorChange: (color: 'white' | 'black' | 'random') => void;
  onGameTimeLimitChange: (minutes: 3 | 5 | 10) => void;
  onResetGame: (startNewGame?: boolean) => void;
  onCloseSetup?: () => void;
  isMobile: boolean;
}

const getModelDisplayName = (selectedModel: string) => {
  switch (selectedModel) {
    case 'gpt-4.1': return 'GPT-4.1';
    case 'gpt-4.5-preview': return 'GPT-4.5';
    case 'claude-3-7-sonnet-20250219': return 'Claude';
    case 'gemini/gemini-pro': return 'Gemini';
    default: return selectedModel;
  }
};

// Helper function to determine if the player won
const didPlayerWin = (
  timeOut: 'player' | 'model' | null,
  isCheckmate: boolean,
  boardOrientation: 'white' | 'black',
  game: any
) => {
  if (timeOut === 'model') return true;
  if (timeOut === 'player') return false;
  
  if (isCheckmate) {
    const winnerIsWhite = game.turn() === 'b';
    const playerIsWhite = boardOrientation === 'white';
    return (winnerIsWhite && playerIsWhite) || (!winnerIsWhite && !playerIsWhite);
  }
  
  return false;
};

const GameControls: React.FC<GameControlsProps> = ({
  selectedModel,
  useVision,
  playerColor,
  gameTimeLimit,
  isGameOver,
  isCheckmate,
  isDraw,
  isGameActive,
  game,
  boardOrientation,
  playerTimeRemaining,
  modelTimeRemaining,
  timeOut,
  activePlayer,
  onModelChange,
  onModelWithVisionChange,
  onPlayerColorChange,
  onGameTimeLimitChange,
  onResetGame,
  onCloseSetup,
  isMobile
}) => {
  // Get the score-related functions from the store
  const { calculateScore, checkHighScore, setShowHighScoreEntry, highScoreSubmitted } = useGameStore();
  
  // Calculate score and check for high score when game is over and player won
  useEffect(() => {
    if (isGameOver && didPlayerWin(timeOut, isCheckmate, boardOrientation, game)) {
      if (!highScoreSubmitted) {
        const score = calculateScore();
        console.log("Player won! Score calculated:", score);
        
        // Check if it's a high score
        const isHighScore = checkHighScore(score);
        if (isHighScore) {
          setShowHighScoreEntry(true);
        }
      }
    }
  }, [isGameOver, timeOut, isCheckmate, boardOrientation, game, calculateScore, checkHighScore, setShowHighScoreEntry, highScoreSubmitted]);

  // Handle model change with vision support
  const handleModelChange = (model: string, useVision: boolean) => {
    onModelWithVisionChange(model, useVision);
  };

  return (
    <div className="bg-black/80 border border-terminal-green p-4 rounded flex flex-col gap-4 min-w-[250px] max-h-[90vh] overflow-y-auto relative z-10 sm:bg-black/50 sm:p-5 sm:gap-5">
      <AnimatePresence mode="wait">
        {isGameActive && !isGameOver ? (
          <motion.div
            key="game-active"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <GameActive
              modelName={getModelDisplayName(selectedModel)}
              modelTimeRemaining={modelTimeRemaining}
              playerTimeRemaining={playerTimeRemaining}
              activePlayer={activePlayer}
              onResign={() => onResetGame(false)}
            />
          </motion.div>
        ) : isGameOver ? (
          <motion.div
            key="game-over"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <GameOver
              modelName={getModelDisplayName(selectedModel)}
              modelTimeRemaining={modelTimeRemaining}
              playerTimeRemaining={playerTimeRemaining}
              timeOut={timeOut}
              isCheckmate={isCheckmate}
              isDraw={isDraw}
              boardOrientation={boardOrientation}
              game={game}
              onNewGame={() => onResetGame(false)}
              isMobile={isMobile}
            />
          </motion.div>
        ) : (
          <motion.div
            key="game-setup"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <GameSetup
              selectedModel={selectedModel}
              playerColor={playerColor}
              useVision={useVision}
              onModelChange={handleModelChange}
              onPlayerColorChange={onPlayerColorChange}
              gameTimeLimit={gameTimeLimit}
              onGameTimeLimitChange={onGameTimeLimitChange}
              onResetGame={onResetGame}
              onClose={onCloseSetup}
              isMobile={isMobile}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GameControls;